import { Link, useLocation } from 'react-router-dom';
import { FileQuestion, ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';

/**
 * Catch-all route (Wave 6 F2).
 *
 * Before this existed an unknown URL rendered a blank white page, which is
 * indistinguishable from a crashed page. That ambiguity contaminated bug
 * reports — TD-130 blamed "Vite rendering flakiness" for blank pages that were
 * in at least some cases simply wrong routes.
 */
export function NotFoundPage() {
  const location = useLocation();

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Page Not Found</h1>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col items-center text-center py-12">
          <FileQuestion className="h-16 w-16 text-gray-400 mb-4" />
          <CardTitle className="text-2xl">This page does not exist</CardTitle>
          <CardDescription className="max-w-md mt-2">
            No route matches{' '}
            <code
              className="rounded bg-gray-100 px-1.5 py-0.5 text-sm text-gray-700"
              data-testid="not-found-path"
            >
              {location.pathname}
            </code>
            . Check the address, or use the navigation to find what you need.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center gap-3 pb-8">
          <Button variant="outline" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
